import { Component, EventEmitter, Input, OnInit, Output, signal, WritableSignal } from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';
import { UiKitModule } from '../../../../shared/ui-kit/ui-kit.module';
import { SwitchOrganizationModels } from '@models/switch-organization.model';
import { ActiveOrganization, UserOrganizationDropDown } from '@models/organization.model';
import { Router } from '@angular/router';
import { filter, map, Observable, take } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { TruncateDirective } from '../../../../shared/directives/truncate.directive';
import { UiButtonComponent, UiAvatarComponent } from '@ui';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../store/app.state';
import { UserSelectors } from '@states/user/user.selector-types';
import { UserModel } from '@models/user.model';
import {
  SwitchOrganizationSupportSearchComponent,
} from './switch-organization-support-search/switch-organization-support-search.component';
import {
  SwitchOrganizationDefaultSearchComponent,
} from './switch-organization-default-search/switch-organization-default-search.component';

// Roles allowed to create a new organization (mirrors the backend DeveloperOrOrgCreatorGuard
// plus operator, which retains UI access). Keep in sync with createOrganizationGuard.
const CREATE_ORGANIZATION_ROLES: UserModel.Role[] = [
  UserModel.Role.DEVELOPER,
  UserModel.Role.OPERATOR,
  UserModel.Role.ORG_CREATOR,
];

@Component({
  selector: 'app-switch-organization',
  standalone: true,
  imports: [
    NgIf,
    AsyncPipe,
    UiKitModule,
    TruncateDirective,
    TranslatePipe,
    UiButtonComponent,
    UiAvatarComponent,
    SwitchOrganizationSupportSearchComponent,
    SwitchOrganizationDefaultSearchComponent,
  ],
  templateUrl: './switch-organization.component.html',
  styleUrl: './switch-organization.component.scss',
})
export class SwitchOrganizationComponent implements OnInit {
  @Input() activeOrganization: ActiveOrganization;
  @Input() userOrganizations: UserOrganizationDropDown[];
  @Input() position: 'left' | 'right' = 'left';

  public canCreateOrg$: Observable<boolean>;
  public isSupport$: Observable<boolean>;

  protected readonly Tab: typeof SwitchOrganizationModels.Tab = SwitchOrganizationModels.Tab;
  protected readonly activeTab: WritableSignal<SwitchOrganizationModels.Tab> = signal(
    SwitchOrganizationModels.Tab.MyOrganizations,
  );

  @Output() onMenuClosed: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private router: Router,
    private store$: Store<AppState>,
  ) {}

  public ngOnInit() {
    const userState$ = this.store$.select(UserSelectors.selectUser).pipe(
      filter(userState => !!userState.isProfileLoaded),
      take(1),
    );

    this.isSupport$ = userState$.pipe(
      map(userState => (userState.roles ?? []).includes(UserModel.Role.SUPPORT)),
    );

    this.canCreateOrg$ = userState$.pipe(
      map(userState =>
        (userState.roles ?? []).some(role => CREATE_ORGANIZATION_ROLES.includes(role as UserModel.Role)),
      ),
    );
  }

  public createOrganization(): void {
    this.router.navigate(['/organization/create-organization/step1']);
    this.onMenuClosed.emit();
  }

  protected setActiveTab(tab: SwitchOrganizationModels.Tab): void {
    this.activeTab.set(tab);
  }
}
